import { BaseHandler, GameConfig } from "@/lib/BaseHandler";
import { CardItem, NarutoItem } from "@/interfaces/CardItem";
import {
  getCardImage,
  getCardData,
  getAltArts,
  getAACardId,
  getAACardIdData,
  getAAFromCardId,
  cleanCardData,
} from "@/lib/games/naruto/getCardDataLocal";
import getDefaultFilters from "@/components/DeckBuilder/game/naruto/filters/getDefaultFilters";
import { filterCards as filterCardsNaruto } from "@/components/DeckBuilder/game/naruto/filters/filterCards";
import { sortCards as narutoSortCards } from "@/lib/games/naruto/deckbuilderSortCards";
import { DeckCardsMap } from "@/lib/games/onePiece/DeckCommonCardsFilter";
import { getImgproxyUrl } from "@/lib/imgproxy";
import { CDN } from "@/lib/cdn";

// Decklist sorting function
function sortDecklist(cardList: string, leader?: string): string {
  const cards = cardList.split(";");

  // combine duplicates like aa arts
  const cardMap = new Map<string, number>();
  for (const card of cards) {
    const [count, cardId] = card.split("x");
    cardMap.set(cardId, (cardMap.get(cardId) || 0) + parseInt(count));
  }

  // Convert cardMap to array of [cardId, count] pairs for sorting
  const cardArray = Array.from(cardMap.entries()).map(([cardId, count]) => {
    const cardData = getCardData(cardId);
    return {
      cardId,
      count,
      cardData: cardData as NarutoItem | undefined,
    };
  });

  // Sort the array based on the specified criteria
  cardArray.sort((a, b) => {
    if (!a.cardData || !b.cardData) return 0;

    // Sort by card_type first
    const typeOrder = ["leader", "character", "jutsu", "mission"];
    const aTypeIndex = typeOrder.indexOf(a.cardData.card_type);
    const bTypeIndex = typeOrder.indexOf(b.cardData.card_type);
    if (aTypeIndex !== bTypeIndex) {
      return aTypeIndex - bTypeIndex;
    }

    // Sort by chakra_cost
    const aChakraCost = a.cardData.chakra_cost || 0;
    const bChakraCost = b.cardData.chakra_cost || 0;
    if (aChakraCost !== bChakraCost) {
      return aChakraCost - bChakraCost;
    }

    // Sort by power
    const aPow = a.cardData.pow || 0;
    const bPow = b.cardData.pow || 0;
    if (aPow !== bPow) {
      return aPow - bPow;
    }

    // Sort by name
    return a.cardData.name.localeCompare(b.cardData.name);
  });

  // Convert back to the original format
  return cardArray.map(({ cardId, count }) => `${count}x${cardId}`).join(";");
}

const narutoConfig: GameConfig = {
  cdnBaseUrl: CDN,
  setImagePath: "/games/naruto/images/set-squares/",
  hasLeader: true,

  cardDataFunctions: {
    getCardData,
    getCardImage: (cardId: string, size?: string, src?: any) =>
      getCardImage(cardId, size as any, src),
    getAltArts,
    getAACardId,
    getAACardIdData,
    getAAFromCardId,
    cleanCardData,
  },

  getDefaultFilters,
  filterCards: (
    cards: CardItem[],
    filters: any,
    defaultFilters: any,
    deckCardsMap: DeckCardsMap,
  ) =>
    filterCardsNaruto(
      cards as NarutoItem[],
      filters,
      defaultFilters,
      deckCardsMap,
    ),
  sortCards: (a: CardItem, b: CardItem) =>
    narutoSortCards(a as NarutoItem, b as NarutoItem),
  sortDecklist,

  // Naruto shows its traits rather than the card type; supplying them through
  // the shared CardModalData hooks keeps the modal free of a game branch.
  getCardType: () => "",
  getAdditionalChips: (card: CardItem) => (card as NarutoItem).traits ?? [],
  getCardEffect: (card: CardItem) => [(card as NarutoItem).effect],
  getCardTrigger: (card: CardItem) => [], // Naruto doesn't have triggers

  getEnergyCost: (card: CardItem) => (card as NarutoItem).chakra_cost ?? 0,
  getColor: (card: CardItem) => (card as NarutoItem).color,
};

export class NarutoHandler extends BaseHandler {
  constructor() {
    super(narutoConfig);
  }

  getSet(card: CardItem): string {
    return (card as NarutoItem).set_id || "";
  }

  getTraits(card: CardItem): string[] {
    return (card as NarutoItem).traits ?? [];
  }

  // Removed getSetImage override - using BaseHandler's .webp implementation
}
